/**
 * useSamplePlayer hook
 *
 * Loads a fixed set of one-shot samples into memory and fires them on
 * demand. Built for soundboards — many short, overlapping hits — rather
 * than the single-track playback that `useAudio` handles.
 *
 * Decoding goes through one shared AudioContext (browsers cap how many
 * can be live at once), and each voice is disposed when it finishes so
 * rapid retriggering doesn't leak nodes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import * as Tone from 'tone';

export interface UseSamplePlayerReturn {
  /** Decode every URL into memory. Safe to call repeatedly. */
  load: () => Promise<void>;
  /** Fire a sample. `pitchRatio` > 1 plays it higher and shorter. */
  play: (url: string, pitchRatio?: number) => void;
  /** Have all samples finished decoding */
  isReady: boolean;
  /** Decoding in flight */
  isLoading: boolean;
  /** Samples decoded so far, for a progress readout */
  loadedCount: number;
  /** Total samples requested */
  totalCount: number;
  /** Decode/network failure, if any */
  error: string | null;
}

export function useSamplePlayer(urls: string[]): UseSamplePlayerReturn {
  const buffersRef = useRef<Map<string, AudioBuffer>>(new Map());
  const activeRef = useRef<Set<Tone.Player>>(new Set());
  const loadPromiseRef = useRef<Promise<void> | null>(null);

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // The URL list is a module-level constant in practice; hold it in a ref
  // so `load` stays stable even if the caller passes a new array identity.
  const urlsRef = useRef(urls);
  urlsRef.current = urls;

  const load = useCallback(async (): Promise<void> => {
    if (loadPromiseRef.current) return loadPromiseRef.current;

    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Browsers require a user gesture before audio can start; the
        // caller invokes this from a click or keypress.
        await Tone.start();

        const context = Tone.getContext().rawContext as AudioContext;
        let done = 0;

        await Promise.all(
          urlsRef.current.map(async (url) => {
            if (!buffersRef.current.has(url)) {
              const response = await fetch(url);
              if (!response.ok) throw new Error(`${response.status} for ${url}`);
              const arrayBuffer = await response.arrayBuffer();
              buffersRef.current.set(url, await context.decodeAudioData(arrayBuffer));
            }
            done += 1;
            setLoadedCount(done);
          }),
        );

        setIsReady(true);
      } catch (e) {
        setError((e as Error).message);
        // Allow a later attempt to retry rather than latching the failure.
        loadPromiseRef.current = null;
      } finally {
        setIsLoading(false);
      }
    };

    loadPromiseRef.current = run();
    return loadPromiseRef.current;
  }, []);

  const play = useCallback((url: string, pitchRatio = 1) => {
    const buffer = buffersRef.current.get(url);
    if (!buffer) return;

    // Tabbing away can suspend the context; bring it back before playing.
    if (Tone.getContext().state === 'suspended') {
      void Tone.getContext().resume();
    }

    const player = new Tone.Player(buffer).toDestination();
    player.playbackRate = pitchRatio;
    player.onstop = () => {
      activeRef.current.delete(player);
      player.dispose();
    };
    activeRef.current.add(player);
    player.start();
  }, []);

  // Tear down any voices still ringing when the page unmounts.
  useEffect(
    () => () => {
      activeRef.current.forEach((player) => player.dispose());
      activeRef.current.clear();
    },
    [],
  );

  return {
    load,
    play,
    isReady,
    isLoading,
    loadedCount,
    totalCount: urls.length,
    error,
  };
}
